import { useEffect, useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Download, Loader2, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { getApiErrorMessage } from '@/services/api/client';
import {
  descargarReporte, getEsquemaReporte, getPreviewReporte,
  type ValoresFiltro,
} from '@/services/api/reportes.api';
import { formatNumber, cn } from '@/lib/utils';
import { ReportFiltros } from './ReportFiltros';
import { ReportPreviewTabla } from './ReportPreviewTabla';

const PAGE_SIZE = 50;

interface ReportDialogProps {
  /** Clave del reporte en el catálogo, p. ej. `inventario-activos`. */
  reporte: string;
  /** Filtros heredados de la pantalla que abrió el diálogo. */
  filtrosIniciales?: ValoresFiltro;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Consulta el reporte antes de descargarlo.
 *
 * La vista previa y la descarga pegan al mismo endpoint con los mismos
 * parámetros, así que ajustar acá y bajar después no depara sorpresas. TanStack
 * cachea por combinación de filtros, de modo que deshacer un ajuste es
 * instantáneo.
 */
export function ReportDialog({
  reporte,
  filtrosIniciales,
  open,
  onOpenChange,
}: ReportDialogProps) {
  const [columnas, setColumnas] = useState<string[]>([]);
  const [filtros, setFiltros] = useState<ValoresFiltro>(filtrosIniciales ?? {});
  const [page, setPage] = useState(1);
  const [descargando, setDescargando] = useState(false);

  const esquemaQuery = useQuery({
    queryKey: ['reporte-esquema', reporte],
    queryFn: () => getEsquemaReporte(reporte),
    enabled: open,
    staleTime: Infinity, // el esquema solo cambia con un despliegue
  });

  const esquema = esquemaQuery.data;

  // Al abrir, arranca con las columnas por defecto y los filtros de la pantalla.
  useEffect(() => {
    if (!open || !esquema) return;
    setColumnas(esquema.columnas.filter((c) => c.porDefecto).map((c) => c.clave));
    setFiltros(filtrosIniciales ?? {});
    setPage(1);
    // `filtrosIniciales` es un literal nuevo en cada render del padre; incluirlo
    // reiniciaría el diálogo mientras se usa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, esquema]);

  const parametros = useMemo(
    () => ({ columnas, filtros, page, pageSize: PAGE_SIZE }),
    [columnas, filtros, page],
  );

  const previewQuery = useQuery({
    queryKey: ['reporte-preview', reporte, parametros],
    queryFn: () => getPreviewReporte(reporte, parametros),
    enabled: open && !!esquema && columnas.length > 0,
    placeholderData: keepPreviousData, // evita el parpadeo al paginar
  });

  const preview = previewQuery.data;
  const total = preview?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function alternarColumna(clave: string) {
    setColumnas((actuales) =>
      actuales.includes(clave)
        ? actuales.filter((c) => c !== clave)
        : // Se reinserta en el orden del esquema para que la tabla no se
          // reordene según el orden en que se hizo clic.
          esquema!.columnas.filter((c) => actuales.includes(c.clave) || c.clave === clave)
            .map((c) => c.clave),
    );
    setPage(1);
  }

  function cambiarFiltro(clave: string, valor: string | string[] | boolean | undefined) {
    setFiltros((actuales) => ({ ...actuales, [clave]: valor }));
    setPage(1);
  }

  function restablecer() {
    if (!esquema) return;
    setColumnas(esquema.columnas.filter((c) => c.porDefecto).map((c) => c.clave));
    setFiltros(filtrosIniciales ?? {});
    setPage(1);
  }

  async function descargar() {
    setDescargando(true);
    try {
      // Sin `page`: la descarga trae todo lo que pasa los filtros, hasta el
      // tope del reporte.
      const nombre = await descargarReporte(reporte, { columnas, filtros });
      toast.success('Reporte descargado', { description: nombre });
    } catch (error) {
      toast.error('No se pudo generar el reporte', {
        description: getApiErrorMessage(error),
      });
    } finally {
      setDescargando(false);
    }
  }

  const excedeTope = !!esquema && total > esquema.maxFilas;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        `flex flex-col` pisa el `grid` del primitivo, y `max-h-[90vh]` es lo que
        impide que el diálogo crezca más que la pantalla: sin él, el contenido
        se desborda por arriba y por abajo (el primitivo centra con
        -translate-y-1/2 y no tiene tope de alto), el pie se solapa con la tabla
        y no hay forma de llegar a lo que quedó fuera.
      */}
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col gap-4">
        {/* pr-8 deja libre la esquina donde el primitivo pone la X de cerrar. */}
        <DialogHeader className="shrink-0 pr-8">
          <DialogTitle>{esquema?.titulo ?? 'Reporte'}</DialogTitle>
          <DialogDescription>
            {esquema?.descripcion ?? 'Cargando configuración del reporte…'}
          </DialogDescription>
        </DialogHeader>

        {esquemaQuery.isError ? (
          <p className="py-8 text-center text-sm text-destructive">
            {getApiErrorMessage(esquemaQuery.error, 'No se pudo cargar el reporte.')}
          </p>
        ) : (
          // `min-h-0` es obligatorio: sin él un hijo flex nunca se encoge por
          // debajo de su contenido y el scroll interno no llega a activarse.
          // En pantallas angostas el grid se apila y hace scroll completo.
          <div className="grid gap-4 lg:grid-cols-[260px_1fr] flex-1 min-h-0
                          overflow-y-auto lg:overflow-hidden">

            {/* ── Panel de ajuste ──────────────────────────────────── */}
            <aside className="space-y-4 min-h-0 lg:overflow-y-auto pr-1">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide">
                  Filtros
                </Label>
                <ReportFiltros
                  filtros={esquema?.filtros ?? []}
                  valores={filtros}
                  onChange={cambiarFiltro}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide">
                  Columnas ({columnas.length}/{esquema?.columnas.length ?? 0})
                </Label>
                <div className="space-y-1">
                  {esquema?.columnas.map((columna) => (
                    <label
                      key={columna.clave}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-input accent-primary-600"
                        checked={columnas.includes(columna.clave)}
                        onChange={() => alternarColumna(columna.clave)}
                      />
                      <span className="truncate">{columna.titulo}</span>
                    </label>
                  ))}
                </div>
              </div>

              <Button variant="outline" size="sm" onClick={restablecer} className="w-full">
                <RotateCcw className="h-3.5 w-3.5" />
                Restablecer
              </Button>
            </aside>

            {/* ── Vista previa ─────────────────────────────────────── */}
            <section className="flex flex-col min-w-0 min-h-0 border rounded-lg">
              <div className="shrink-0 flex items-center justify-between gap-3 border-b px-3 py-2">
                <p className="text-xs text-muted-foreground">
                  {previewQuery.isPending
                    ? 'Consultando…'
                    : `${formatNumber(total)} registro${total === 1 ? '' : 's'}`}
                  {excedeTope && (
                    <span className="ml-2 text-amber-600 font-medium">
                      · el archivo traerá los primeros {formatNumber(esquema!.maxFilas)}
                    </span>
                  )}
                </p>

                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost" size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {page} / {totalPaginas}
                  </span>
                  <Button
                    variant="ghost" size="sm"
                    disabled={page >= totalPaginas}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className={cn('flex-1 min-h-0 overflow-auto', previewQuery.isFetching && 'opacity-60')}>
                {previewQuery.isError ? (
                  <p className="p-8 text-center text-sm text-destructive">
                    {getApiErrorMessage(previewQuery.error, 'No se pudo consultar el reporte.')}
                  </p>
                ) : (
                  <ReportPreviewTabla
                    columnas={preview?.columnas ?? []}
                    filas={preview?.filas ?? []}
                    cargando={previewQuery.isPending && columnas.length > 0}
                  />
                )}
              </div>
            </section>
          </div>
        )}

        <div className="shrink-0 flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
          <Button onClick={descargar} disabled={descargando || columnas.length === 0}>
            {descargando
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Download className="h-4 w-4" />}
            Descargar Excel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
